import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PresetSegmentsTail from './PresetSegmentsTail';
import usePresetSubscription from 'modules/Common/hooks/usePresetSubscription';
import { formatNumber } from 'helpers/numberFormat';

// Shared segment-pill strip primitive. Every declarant table's
// "smart segments" row — Suppliers, Installations, Imports, Companies,
// Emissions, Benchmarks, Analytics, Reports, Quotes,
// Procurement and the Chase gap segments — renders through this one
// component so the pill styling (aria-pressed, active bg-white/border/
// shadow-sm, the count/icon/indicator slots) is defined exactly once.
// This is pure consolidation: the markup below is byte-for-byte the
// SegmentPill each strip used to inline.
//
// A segment is:
//   { id, label, icon?, indicator?, dot?, count?, dividerBefore? }
//   - icon:          FontAwesome icon rendered before the label
//   - indicator:     arbitrary node rendered before the icon (e.g. a
//                    status dot with a utility-class fill)
//   - dot:           shorthand for an inline-colored status dot indicator
//   - count:         trailing muted number (localized; hidden when the
//                    value is not a number)
//   - dividerBefore: render a subtle "|" separator before this pill
//
// Preset integration: when `presetTail` is set the strip renders the
// shared PresetSegmentsTail AND dims the built-in pills while a saved
// preset is the active view (matchingPresetId), reproducing the old
// per-strip `!matchingPresetId && …` active guard. Strips that don't use
// presets (Reports, Analytics, Chase) leave `presetTail` false and, for
// Analytics, pass a custom `tail` node.

export const formatCount = (n) =>
  typeof n === 'number' && Number.isFinite(n) ? formatNumber(n) : '0';

// Inline-colored status dot — matches the SuppliersStatusBar / chase dots
// so the segments and status bars read as one continuous control.
export const SegmentDot = ({ color }) => (
  <span
    className="w-1.5 h-1.5 rounded-full"
    style={{ backgroundColor: color }}
  />
);

export const CgSegmentPill = ({
  active,
  label,
  count,
  icon,
  indicator,
  dot,
  truncate = false,
  onClick,
  ...rest
}) => (
  <button
    type="button"
    {...rest}
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${
      active
        ? 'bg-white text-slate-900 border border-slate-300 shadow-sm'
        : 'text-slate-500 hover:text-slate-700 border hover:bg-slate-100'
    }`}
  >
    {indicator}
    {dot && <SegmentDot color={dot} />}
    {icon && (
      <FontAwesomeIcon
        icon={icon}
        className="text-[11px]"
      />
    )}
    {truncate ? <span className="truncate max-w-[140px]">{label}</span> : label}
    {typeof count === 'number' && (
      <span className="text-[11px] text-slate-400 font-normal">{formatCount(count)}</span>
    )}
  </button>
);

const Divider = () => <span className="text-slate-200 mx-1">|</span>;

export default function CgSegmentPills({
  segments = [],
  activeSegmentId,
  onSegmentChange,
  presetTail = false,
  truncateLabels = false,
  tail = null,
  className = 'flex items-center gap-1 flex-wrap',
}) {
  // Always called (hooks rules) but only consulted when a preset tail is
  // present — a bare window listener is a no-op for preset-less strips.
  const { matchingPresetId } = usePresetSubscription();
  const suppress = presetTail && Boolean(matchingPresetId);

  return (
    <div className={className}>
      {segments.map((seg) => (
        <React.Fragment key={seg.id || seg.label}>
          {seg.dividerBefore && <Divider />}
          <CgSegmentPill
            active={!suppress && activeSegmentId === seg.id}
            label={seg.label}
            icon={seg.icon}
            indicator={seg.indicator}
            dot={seg.dot}
            count={seg.count}
            truncate={truncateLabels}
            onClick={() => onSegmentChange(seg.id)}
          />
        </React.Fragment>
      ))}
      {tail}
      {presetTail && <PresetSegmentsTail />}
    </div>
  );
}
